import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function DurhamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Durham Pothole Claims | 198,000 Repairs in Five Years | Fixtyer"
        description="Durham County Council filled an estimated 198,463 potholes in five years and rejects 95% of liability claims — yet earns an AMBER DfT condition rating. Resurfacing has halved since 2020/21. See what the data means for your claim."
        canonical="/councils/durham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Durham', url: '/councils/durham' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Durham spends above its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect, not aggregate spend. Durham is AMBER on condition and best practice, fills roughly 40,000 potholes a year, and admits resurfacing output has halved since 2020/21 despite static budgets. Section 58 is about whether your particular pothole was reasonably inspected and repaired — not whether the council outspends its DfT grant.',
          },
          {
            question: 'What if my pothole was on an unclassified rural road?',
            answer: 'U-roads make up 2,325km — 60% of Durham\'s 3,843km network — and are assessed by Coarse Visual Inspection (CVI), not SCANNER. Durham\'s own report admits CVI carries "potential for human error" and that SCANNER rulesets are "flawed" for authorities with a high percentage of rural network. At the last survey, 17% of U-roads were in RED condition — down from 25% in 2022, but still roughly 395km of residential and rural routes needing replacement-level maintenance.',
          },
          {
            question: 'Durham rejects 95% of liability claims — does that mean I should not bother?',
            answer: 'No — it tells you the council fights claims aggressively and will lean on Section 58. Durham\'s own transparency report states a "repudiation rate of 95%" on third-party public liability claims. That is not a measure of claim validity; it is a measure of refusal rate. A well-evidenced claim citing prior reports, photographic proof of defect age, and Durham\'s own admissions on survey limitations is exactly what generic rejections are designed to filter out.',
          },
          {
            question: 'Durham says U-road condition improved from 25% to 17% RED — does that weaken my claim?',
            answer: 'It depends on timing and evidence, not the headline. The 25% figure is from 2022; the 17% is from 2024 — both measured under CVI, which Durham itself notes can involve human error. Even at 17%, roughly one in six unclassified roads is in RED condition. If your incident predates the improvement, or your road was not recently CVI-surveyed, the network-level trend does not prove your specific defect was known and ignored.',
          },
          {
            question: 'Durham admits SCANNER and CVI condition surveys can be flawed — how does that help?',
            answer: 'It undermines blanket Section 58 defences. Durham\'s report states SCANNER rulesets are "inflexible" and "can be flawed" for rural authorities, and that CVI surveys carry "potential for human error". If the council\'s own condition data may not accurately reflect your road, it cannot confidently argue it had reasonable knowledge of every defect — especially on the 60% of its network assessed by eye from a van.',
          },
          {
            question: 'Resurfacing has halved since 2020/21 — does that mean fewer potholes?',
            answer: 'The opposite. Durham resurfaced an estimated 136.91 linear miles in 2020/21 but just 71.92 miles in 2024/25 — a 47% drop — while pothole repairs rose from 34,286 to 43,044 over the same period. The council attributes the resurfacing decline to inflation on labour, plant and materials. Less preventative work and more reactive patching is exactly the pattern that produces the potholes claimants hit.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Durham County Council"
        councilSlug="durham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/durham"
        headline="Durham County Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Durham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Durham: 198,000 Pothole Repairs, 95% Claims Refused
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Durham County Council earns a GREEN spend scorecard and invests beyond its DfT allocation — yet
            the overall rating is <strong className="text-amber-400">AMBER</strong> because condition and best
            practice lag behind. The council filled an estimated{' '}
            <strong className="text-rose-400">198,463 potholes in five years</strong>, resurfacing output has
            halved, and Durham publicly admits its own condition surveys can be flawed on its rural network.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">95%</div>
            <div className="text-white font-medium mt-2">Third-party liability claims repudiated</div>
            <div className="text-brand-300 text-sm mt-1">
              Durham's own transparency report states a 95% repudiation rate on public liability claims —
              while still filling 43,044 potholes in 2024/25 alone across a 3,843km network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Durham's own transparency report — classified
            roads broadly stable, unclassified roads still carrying heavy RED load
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (417km — 10.8% of network): stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2.8%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 3.4% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">20.2%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">77.0%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 76.5%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Durham states its classified network is "slightly better than the national average." A-roads are
              surveyed by SCANNER on a two-year cycle. Credit where due — but A-roads are barely one-tenth of
              the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,101km — 28.6% of network): mixed</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">20.6%</td>
                    <td className="px-4 py-2 text-center">72.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.4%</td>
                    <td className="px-4 py-2 text-center">26.1%</td>
                    <td className="px-4 py-2 text-center">70.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.4%</td>
                    <td className="px-4 py-2 text-center">25.5%</td>
                    <td className="px-4 py-2 text-center">71.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.5%</td>
                    <td className="px-4 py-2 text-center">23.0%</td>
                    <td className="px-4 py-2 text-center">74.5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.0%</td>
                    <td className="px-4 py-2 text-center font-bold">24.2%</td>
                    <td className="px-4 py-2 text-center font-bold">72.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads peaked at 3.4% in 2021 and 2022. Combined RED and amber stood at 27.2%
              in 2024 — more than a quarter of the B/C network needing or approaching maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Preventative Output Is Shrinking</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£21.4m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£27.2m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">81%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Durham outspends its DfT allocation — yet admits{' '}
              <span className="text-rose-400">"the works required far exceed the budget available"</span> and
              resurfacing mileage has halved since 2020/21. The GREEN spend rating measures investment intent,
              not whether the network is keeping pace with deterioration.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,325km Unclassified Network
          </h2>
          <p className="text-surface-600 mb-8">
            60% of Durham's roads assessed by Coarse Visual Inspection — not SCANNER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">CVI, Not SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                Durham uses Coarse Visual Inspection for unclassified roads — two competent operators in a van
                recording visible defects. Classified A and B/C roads are surveyed by SCANNER on a two-year
                cycle. That means network-level condition knowledge on 60% of Durham's roads depends on
                visual assessment, not laser scanning.
              </p>
              <p className="text-surface-600 text-sm">
                Even at the improved 17% RED figure, roughly <strong>395km of U-roads</strong> need
                replacement-level maintenance — estate streets, village routes and rural lanes across County
                Durham.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Council's Own Caveat</h3>
              <p className="text-amber-800 text-sm mb-3">
                Durham states its unclassified network "has improved from 25% in 2022, where maintenance should
                be considered, to 11% in 2025" — using a broader maintenance threshold than the RED category
                alone. The published RED percentages above are the DfT RDC01 figures.
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, a new PAS 2161 methodology will replace the current three-band system with five
                categories — another break in comparability to watch for.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Durham's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI-surveyed — and how current is that data?</li>
              <li>• If 17-25% of U-roads were RED at recent surveys, what was done about yours specifically?</li>
              <li>• Does AI camera data from inspector vans cover your route, or only scheduled corridors?</li>
              <li>• Can the council confidently claim detailed knowledge on a network it assesses by eye?</li>
            </ul>
            <p className="text-white font-medium">
              Durham supplements CVI with AI cameras in 16 highway inspector vans — but that is reactive
              coverage on driven routes, not comprehensive network measurement.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            198,463 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume rising while preventative resurfacing falls — the signature of a network
            running to catch up
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Resurfacing (linear miles)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">34,286</td>
                  <td className="px-6 py-4 text-center">136.91</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">36,050</td>
                  <td className="px-6 py-4 text-center">108.35</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">39,557</td>
                  <td className="px-6 py-4 text-center">79.71</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">45,526</td>
                  <td className="px-6 py-4 text-center">74.81</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">43,044</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">71.92</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total / latest</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">198,463</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">−47% since 2020/21</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~109 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Durham fills around 109 potholes per day. The council anticipates
                45,000 repairs in 2025/26. A network producing defects at that rate is one where potholes
                routinely form between inspections — exactly the scenario where prior reports and photographic
                evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Less Resurfacing, More Patching</h3>
              <p className="text-amber-800 text-sm">
                Durham's own report: "the areas/lengths treated over the previous 5 years has decreased even
                though our budgets for highway maintenance activities have remained reasonably static" — driven
                by inflation on labour, plant and materials. Reactive patching without preventative resurfacing
                is the cycle that creates repeat potholes on the same stretch of road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Survey Flaws Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Durham Admits Its Own Surveys Can Be Flawed
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own assessment of SCANNER and CVI — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="I would suggest the current mechanisms for gathering condition data, SCANNER and CVI can be flawed due to the inflexibility of the rulesets within SCANNER. Particularly in highway authorities, similar to Durham, with a high percentage of rural network. There is also the potential for human error when undertaking the CVI condition surveys."
              source="Durham County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Investment: Maintaining the highway network is costly, the works required far exceed the budget available therefore all potential schemes are prioritised taking into consideration several factors including condition data, road hierarchy, accident history, Highway Inspector recommendations, Members concerns and Customer reports."
              source="Durham County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Like all highway authorities unfortunately there isn't enough budget to allow us to bring all our highway network to green condition."
              source="Durham County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                Durham formally acknowledges that its primary condition assessment tools may not accurately
                reflect reality on its rural network — and that budget constraints mean not every road can
                reach green condition regardless.
              </p>
              <p className="text-surface-600 text-sm">
                That is documented knowledge that blanket Section 58 defences — "we inspect our network
                regularly and had no reason to know about this defect" — may not withstand scrutiny when
                paired with prior reports and photographic evidence.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 95% Repudiation Rate</h3>
              <p className="text-amber-800 text-sm mb-3">
                Durham states: "the authority has a repudiation rate of 95%, this is the 3rd party public
                liability claims that are refuted and not paid." That is presented as a performance metric —
                lower insurance premiums, fewer payouts.
              </p>
              <p className="text-amber-800 text-sm">
                For claimants, it signals an aggressive refusal strategy. Generic claims without prior-report
                evidence or defect-age photography will almost certainly join that 95%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Durham invests seriously — but fights claims hard. Here is how that changes
            your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeds DfT allocation (£27.2m vs £21.4m projected 2025/26)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ~81-86% of spend classed as preventative over six years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition stable and slightly above national average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AI cameras in 16 inspector vans supplementing SCANNER/CVI
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 95% claim repudiation rate — experienced, resourced defence team
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence. Durham knows how to refuse claims and will cite its
                inspection regime and AI investment.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 17-25% of U-roads in RED at recent surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 60% of network assessed by CVI with admitted human-error potential
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits SCANNER rulesets are "flawed" for rural networks
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 198,463 pothole repairs in five years — defects form faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing halved (136.91 → 71.92 miles) while pothole fills rose 26%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> "Works required far exceed the budget available" — council's own words
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a 95% refusal rate, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the CVI survey limitations and admitted human-error risk are your strongest structural arguments</li>
              <li>• Whether Durham's AI inspector coverage actually includes your route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Durham's own transparency data — including its admissions on survey flaws — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in County Durham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A council that refuses 95% of claims demands a well-built case. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No CVI survey-flaw argument</li>
                <li>• No prior-report search</li>
                <li>• No repudiation-rate context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED condition documented</li>
                <li>✅ SCANNER/CVI flaw admissions cited</li>
                <li>✅ 198,463 repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Durham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=durham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Durham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Durham spends above its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect, not aggregate
                spend. Durham is AMBER on condition and best practice, fills roughly 40,000 potholes a year, and
                admits resurfacing output has halved since 2020/21 despite static budgets. Section 58 is about
                whether your particular pothole was reasonably inspected and repaired — not whether the council
                outspends its DfT grant.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified rural road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 2,325km — 60% of Durham's 3,843km network — and are assessed by Coarse Visual
                Inspection (CVI), not SCANNER. Durham's own report admits CVI carries "potential for human error"
                and that SCANNER rulesets are "flawed" for authorities with a high percentage of rural network. At
                the last survey, 17% of U-roads were in RED condition — down from 25% in 2022, but still roughly
                395km of residential and rural routes needing replacement-level maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Durham rejects 95% of liability claims — does that mean I should not bother?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it tells you the council fights claims aggressively and will lean on Section 58. Durham's own
                transparency report states a "repudiation rate of 95%" on third-party public liability claims. That
                is not a measure of claim validity; it is a measure of refusal rate. A well-evidenced claim citing
                prior reports, photographic proof of defect age, and Durham's own admissions on survey limitations
                is exactly what generic rejections are designed to filter out.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Durham says U-road condition improved from 25% to 17% RED — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It depends on timing and evidence, not the headline. The 25% figure is from 2022; the 17% is from
                2024 — both measured under CVI, which Durham itself notes can involve human error. Even at 17%,
                roughly one in six unclassified roads is in RED condition. If your incident predates the
                improvement, or your road was not recently CVI-surveyed, the network-level trend does not prove
                your specific defect was known and ignored.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Durham admits SCANNER and CVI condition surveys can be flawed — how does that help?
              </h3>
              <p className="text-surface-600 text-sm">
                It undermines blanket Section 58 defences. Durham's report states SCANNER rulesets are "inflexible"
                and "can be flawed" for rural authorities, and that CVI surveys carry "potential for human error".
                If the council's own condition data may not accurately reflect your road, it cannot confidently
                argue it had reasonable knowledge of every defect — especially on the 60% of its network assessed
                by eye from a van.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Resurfacing has halved since 2020/21 — does that mean fewer potholes?
              </h3>
              <p className="text-surface-600 text-sm">
                The opposite. Durham resurfaced an estimated 136.91 linear miles in 2020/21 but just 71.92 miles
                in 2024/25 — a 47% drop — while pothole repairs rose from 34,286 to 43,044 over the same period.
                The council attributes the resurfacing decline to inflation on labour, plant and materials. Less
                preventative work and more reactive patching is exactly the pattern that produces the potholes
                claimants hit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.durham.gov.uk/media/46973/Department-for-Transport-Pothole-Return/pdf/DepartmentForTransportPotholeReturn.pdf?m=1750936909943"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Durham County Council Department for Transport Pothole Return (2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
